import { FC, useState } from "react";
import { styled } from "@mui/material/styles";
import { accent, primary, warm } from "style/color";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import SyncIcon from "@mui/icons-material/Sync";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { ICharity } from "interfaces/charity";
import { useNotification, useUpdate } from "@refinedev/core";
import awsExports from "aws-exports";
import { axiosInstance } from "providers/rest-data-provider/utils";

export const CustomCard = styled(Card)({
  "& :hover": {
    backgroundColor: warm[500],
  },
  "& :hover button:not(.charity-sync-sage)": {
    backgroundColor: "transparent",
  },
});

interface CharityCardProps {
  charity: ICharity;
  refresh: () => void;
}

interface SyncDialog {
  open: boolean;
  title: string;
  message: string;
}

const API_BASE_URL = awsExports.endpoints[0].endpoint;

const CLOSED_DIALOG: SyncDialog = { open: false, title: "", message: "" };

const SAGE_ENABLED =
  process.env.REACT_APP_SAGE_INTACCT_FEATURES_ENABLED === "true";

const CharityCard: FC<CharityCardProps> = ({ charity, refresh }) => {
  const { mutate } = useUpdate();
  const { open: openNotification } = useNotification();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncDialog, setSyncDialog] = useState<SyncDialog>(CLOSED_DIALOG);

  const handleVisibilityClick = () => {
    mutate(
      {
        resource: `charities`,
        values: { isBlocked: !charity.isBlocked },
        id: charity.ein,
      },
      { onSuccess: () => refresh() }
    );
  };

  const openUrlInNewTab = (url: string) => {
    if (!/^https?:\/\//i.test(url)) {
      url = "http://" + url;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleMoreInfoClick = (e: any) => {
    e.preventDefault();
    if (charity.organizationUrl) {
      openUrlInNewTab(charity.organizationUrl);
    }
  };

  const handleSyncCharity = async () => {
    setIsSyncing(true);
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/admin/charities/${encodeURIComponent(charity.ein)}/sync-sage`
      );
      const result = response.data;

      if (!result.success) {
        setSyncDialog({
          open: true,
          title: "Sync Failed",
          message: `Sync Charity request failed: ${result.message}`,
        });
        return;
      }

      if (result.matchType === "ein_match") {
        setSyncDialog({
          open: true,
          title: "Vendor Already Exists",
          message: `A vendor with that EIN (ID: ${result.vendorId}) already exists in Sage Intacct`,
        });
        refresh();
        return;
      }

      if (result.matchType === "name_match") {
        setSyncDialog({
          open: true,
          title: "Vendor Already Exists",
          message: `A vendor with name "${charity.name}" (ID: ${result.vendorId}) already exists in Sage Intacct`,
        });
        return;
      }

      // created
      openNotification?.({
        type: "success",
        message: "Charity synced to Sage Intacct successfully.",
        description: result.vendorId
          ? `Vendor ID: ${result.vendorId}`
          : undefined,
      });
      refresh();
    } catch (error: any) {
      setSyncDialog({
        open: true,
        title: "Sync Failed",
        message: `Sync Charity request failed: ${
          error?.response?.data?.message ?? error?.message ?? "An unexpected error occurred."
        }`,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const showSyncButton = SAGE_ENABLED && (charity.recommended || !charity.isBlocked);

  return (
    <>
      <CustomCard>
        <CardContent sx={{ "&:last-child": { pb: 5 } }}>
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems={"center"}
            >
              <Typography
                variant="smallCopy"
                sx={{ color: "neutral.main" }}
              >
                {charity.ein}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {showSyncButton && (
                  <>
                    {charity.sageIntacctVendorId ? (
                      <Typography
                        variant="smallCopy"
                        sx={{ color: "neutral.main", mr: 0.5 }}
                      >
                        Sage Intacct Vendor ID: {charity.sageIntacctVendorId}
                      </Typography>
                    ) : (
                      <Button
                        onClick={handleSyncCharity}
                        disabled={isSyncing}
                        size="small"
                        variant="contained"
                        color="primary"
                        sx={{
                          backgroundColor: primary[500],
                          "&:hover": { backgroundColor: primary[700] },
                          "& *": { backgroundColor: "transparent !important" },
                        }}
                        startIcon={
                          isSyncing ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <SyncIcon fontSize="small" />
                          )
                        }
                        className="charity-sync-sage"
                      >
                        Sync Charity
                      </Button>
                    )}
                  </>
                )}
                <IconButton
                  onClick={handleVisibilityClick}
                  className="charities-unsaved"
                  size="small"
                  sx={{ color: accent[500] }}
                >
                  {charity.isBlocked ? (
                    <VisibilityOffOutlinedIcon />
                  ) : (
                    <VisibilityOutlinedIcon />
                  )}
                </IconButton>
              </Box>
            </Stack>
          </Box>
          <Box sx={{ minHeight: "120px" }}>
            <Typography
              gutterBottom
              sx={{
                color: "neutral.main",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
                whiteSpace: "pre-wrap",
                height: "54px",
              }}
              variant="subtitle1"
            >
              {charity.name}
            </Typography>
            <Typography
              paragraph
              sx={{
                textTransform: "lowercase",
                color: "neutral.main",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
                whiteSpace: "pre-wrap",
              }}
              variant="smallCopyBold"
            >
              {charity.mission}
            </Typography>
          </Box>
          <Typography
            paragraph
            sx={{ color: "neutral.main" }}
            variant="smallCopyBold"
          >
            {charity.street} {charity.street2},<br />
            {charity.city}, {charity.state} {charity.zip}, {charity.country}
          </Typography>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems={"center"}
          >
            <Typography
              variant="bodyCopyBold"
              sx={{ color: "neutral.main" }}
            >
              {charity.cause}
            </Typography>
            {charity.recommended ? (
              <Button
                target="_blank"
                onClick={handleMoreInfoClick}
                href={`${charity.organizationUrl}`}
                startIcon={<LaunchIcon />}
                variant="text"
                color="primary"
                className="charites-more-info"
                disabled={
                  charity.organizationUrl === "" ||
                  charity.organizationUrl === undefined
                }
              >
                More info
              </Button>
            ) : (
              <Button
                target="_blank"
                href={`https://www.charitynavigator.org/ein/${charity.ein}`}
                startIcon={<LaunchIcon />}
                variant="text"
                color="primary"
                className="charites-more-info"
              >
                More info
              </Button>
            )}
          </Stack>
        </CardContent>
      </CustomCard>

      <Dialog
        open={syncDialog.open}
        onClose={() => setSyncDialog(CLOSED_DIALOG)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{syncDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{syncDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialog(CLOSED_DIALOG)} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CharityCard;
